import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Flame, Search } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Admin "Deal of the Day" manager. Lists every product, lets the admin mark any
 * product as a deal with a discount %, and shows the resulting discounted price.
 * Deals appear in the shopper-facing carousel on top of the Mapbazar map.
 */
export default function AdminDeals() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [pct, setPct] = useState({});      // productId -> discount input value
  const [saving, setSaving] = useState({}); // productId -> bool

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/products", { params: q ? { q } : {} });
      setProducts(Array.isArray(data) ? data : []);
      const init = {};
      (data || []).forEach((p) => { init[p.id] = p.deal_discount_pct || 20; });
      setPct((prev) => ({ ...init, ...prev }));
    } catch (e) {
      toast.error("Could not load products");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const setDeal = async (p, makeDeal) => {
    const discount = Math.max(1, Math.min(95, parseInt(pct[p.id], 10) || 0));
    setSaving((s) => ({ ...s, [p.id]: true }));
    try {
      const { data } = await api.patch(`/admin/products/${p.id}/deal`, {
        is_deal: makeDeal,
        discount_pct: makeDeal ? discount : 0,
      });
      setProducts((list) => list.map((x) => x.id === p.id
        ? { ...x, is_deal: data.is_deal, deal_discount_pct: data.deal_discount_pct, deal_price: data.deal_price }
        : x));
      toast.success(makeDeal ? `Added “${p.name}” as a deal (${discount}% off)` : `Removed “${p.name}” from deals`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update deal");
    } finally {
      setSaving((s) => ({ ...s, [p.id]: false }));
    }
  };

  const dealsCount = products.filter((p) => p.is_deal).length;

  return (
    <div className="space-y-4" data-testid="admin-deals">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Flame className="w-5 h-5 text-rose-600" /> Deal of the Day
          </h3>
          <p className="text-sm text-slate-500">{dealsCount} active deal{dealsCount === 1 ? "" : "s"} · shown in the shopper carousel on top of the map</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="admin-deals-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search products…"
            className="pl-9 rounded-xl w-60"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading products…</div>
      ) : products.length === 0 ? (
        <div className="text-sm text-slate-500">No products found.</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {products.map((p) => (
            <Card key={p.id} className={`rounded-2xl ${p.is_deal ? "border-rose-200 bg-rose-50/40" : "border-slate-100"}`} data-testid={`admin-deal-row-${p.id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                  {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-xl">🛍️</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 text-sm truncate">{p.name}</div>
                  <div className="text-xs text-slate-500 truncate">{p.shop_name} · {p.category || "Local"}</div>
                  <div className="text-sm mt-0.5">
                    {p.is_deal ? (
                      <span className="font-bold text-rose-700">₹{p.deal_price} <span className="text-slate-400 line-through font-normal">₹{p.price}</span> <span className="text-rose-600 font-bold">{p.deal_discount_pct}% off</span></span>
                    ) : (
                      <span className="font-semibold text-slate-700">₹{p.price}</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min={1}
                      max={95}
                      value={pct[p.id] ?? 20}
                      onChange={(e) => setPct((s) => ({ ...s, [p.id]: e.target.value }))}
                      className="w-16 h-8 rounded-lg text-center text-sm"
                      data-testid={`admin-deal-pct-${p.id}`}
                    />
                    <span className="text-xs text-slate-400">%</span>
                  </div>
                  {p.is_deal ? (
                    <Button size="sm" variant="outline" className="rounded-lg h-8 text-rose-700 border-rose-200" disabled={saving[p.id]} onClick={() => setDeal(p, false)} data-testid={`admin-deal-remove-${p.id}`}>
                      Remove
                    </Button>
                  ) : (
                    <Button size="sm" className="rounded-lg h-8 bg-rose-600 hover:bg-rose-700" disabled={saving[p.id]} onClick={() => setDeal(p, true)} data-testid={`admin-deal-add-${p.id}`}>
                      Make deal
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
